import { formatCompact } from './TrendChart';

export interface CompareItem {
  label: string;
  value: number;
  /** Second measure for the same item, e.g. users beside sessions. Optional. */
  secondary?: number;
}

interface CompareBarsProps {
  items: CompareItem[];
  /** Names the primary measure — the legend when there is one series. */
  valueLabel: string;
  secondaryLabel?: string;
  /** Renders each value as a share of the total as well as an absolute. */
  showShare?: boolean;
  /** The value text. Default: {@link formatCompact}; money and credits pass their own. */
  formatValue?: (value: number) => string;
}

/**
 * A handful of named things compared on one or two measures.
 *
 * <b>Horizontal, because the labels are domains.</b> Vertical bars would force
 * <c>tavi.anyware.software</c> to be rotated or truncated; horizontal gives the label a full line
 * and keeps the value axis where the eye already scans.
 *
 * <b>Two measures use the two VALIDATED categorical slots</b> (`--series-1` / `--series-2`, CVD ΔE
 * 29.9). There is no third slot — a third measure belongs in a second chart or a table, not in an
 * invented hue.
 *
 * Share is shown beside the absolute rather than instead of it: "48%" alone hides whether that is
 * 48% of 20 sessions or of 20,000, and at this site's volume that distinction is the whole story.
 */
export function CompareBars({ items, valueLabel, secondaryLabel, showShare = true, formatValue = formatCompact }: CompareBarsProps) {
  if (items.length === 0) {
    return <div className="empty-state t-body">Nothing to compare yet.</div>;
  }

  const max = Math.max(...items.map((i) => Math.max(i.value, i.secondary ?? 0)), 1);
  const total = items.reduce((sum, i) => sum + i.value, 0);
  const hasSecondary = items.some((i) => i.secondary !== undefined);

  return (
    <div className="compare">
      {hasSecondary && (
        <div className="compare-legend t-caption">
          <span className="compare-key"><i style={{ background: 'var(--series-1)' }} />{valueLabel}</span>
          <span className="compare-key"><i style={{ background: 'var(--series-2)' }} />{secondaryLabel}</span>
        </div>
      )}

      <ul className="compare-list">
        {items.map((item) => (
          <li key={item.label} className="compare-row">
            <div className="compare-head">
              <span className="compare-label" title={item.label}>{item.label}</span>
              <span className="compare-value t-label tabular">
                {formatValue(item.value)}
                {showShare && total > 0 && (
                  <span className="subtle"> · {Math.round((item.value / total) * 100)}%</span>
                )}
              </span>
            </div>

            <div className="compare-track">
              <span
                className="compare-fill"
                style={{ width: `${Math.max(1, (item.value / max) * 100)}%`, background: 'var(--series-1)' }}
              />
            </div>

            {item.secondary !== undefined && (
              <div className="compare-track compare-track-secondary">
                <span
                  className="compare-fill"
                  style={{ width: `${Math.max(1, (item.secondary / max) * 100)}%`, background: 'var(--series-2)' }}
                />
                <span className="compare-secondary-value t-caption subtle tabular">
                  {formatValue(item.secondary)}
                </span>
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
